/**
 * usePagination Hook
 *
 * Manages pagination state with page, limit, and total tracking.
 * Supports both traditional pagination and infinite scroll modes.
 */

import { useState, useCallback, useMemo } from 'react';
import { PaginationState, PaginationConfig } from '../types';

export interface UsePaginationOptions extends PaginationConfig {
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

export interface UsePaginationReturn {
  pagination: PaginationState;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startItem: number;
  endItem: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setTotal: (total: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  goToPage: (page: number) => void;
  reset: () => void;
}

export function usePagination(options: UsePaginationOptions = {}): UsePaginationReturn {
  const {
    defaultPage = 1,
    defaultLimit = 20,
    pageSizeOptions = [10, 20, 50, 100],
    onPageChange,
    onLimitChange,
  } = options;

  const [pagination, setPagination] = useState<PaginationState>({
    page: defaultPage,
    limit: defaultLimit,
    total: 0,
  });

  const totalPages = useMemo(
    () => Math.ceil(pagination.total / pagination.limit) || 1,
    [pagination.total, pagination.limit]
  );

  const hasNextPage = pagination.page < totalPages;
  const hasPreviousPage = pagination.page > 1;

  const startItem = useMemo(
    () => (pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1),
    [pagination.page, pagination.limit, pagination.total]
  );

  const endItem = useMemo(
    () => Math.min(pagination.page * pagination.limit, pagination.total),
    [pagination.page, pagination.limit, pagination.total]
  );

  const setPage = useCallback(
    (page: number) => {
      const validPage = Math.max(1, Math.min(page, totalPages));
      setPagination((prev) => ({ ...prev, page: validPage }));
      onPageChange?.(validPage);
    },
    [totalPages, onPageChange]
  );

  const setLimit = useCallback(
    (limit: number) => {
      // Validate limit is in allowed options
      const validLimit = pageSizeOptions.includes(limit) ? limit : defaultLimit;
      setPagination((prev) => ({
        ...prev,
        limit: validLimit,
        page: 1, // Reset to page 1 when limit changes
      }));
      onLimitChange?.(validLimit);
    },
    [pageSizeOptions, defaultLimit, onLimitChange]
  );

  const setTotal = useCallback((total: number) => {
    setPagination((prev) => {
      const newTotalPages = Math.ceil(total / prev.limit) || 1;
      // Adjust current page if it's now beyond total pages
      const adjustedPage = prev.page > newTotalPages ? newTotalPages : prev.page;
      return { ...prev, total, page: adjustedPage };
    });
  }, []);

  const nextPage = useCallback(() => {
    if (hasNextPage) {
      setPage(pagination.page + 1);
    }
  }, [hasNextPage, pagination.page, setPage]);

  const previousPage = useCallback(() => {
    if (hasPreviousPage) {
      setPage(pagination.page - 1);
    }
  }, [hasPreviousPage, pagination.page, setPage]);

  const goToPage = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const reset = useCallback(() => {
    setPagination({
      page: defaultPage,
      limit: defaultLimit,
      total: 0,
    });
  }, [defaultPage, defaultLimit]);

  return {
    pagination,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    startItem,
    endItem,
    setPage,
    setLimit,
    setTotal,
    nextPage,
    previousPage,
    goToPage,
    reset,
  };
}

export default usePagination;
